import React from 'react';
import Image from 'next/image';
import { Popover } from 'antd';
import { getIsOverseaByCountryCode } from '$/common/utils/countryCodeUtil';
import { useOfficialHomeContext } from '@/components/overseaHome/OfficialHomeContext';
import discordIcon from './images/discord.png';
import appStoreBadge from './images/app-store.svg';
import googlePlayBadge from './images/google-play.svg';
// import insIcon from './images/ins.png';
import xIcon from './images/x.png';
// import tiktokIcon from './images/tiktok.png';
// import youtubeIcon from './images/youtube.png';
import mailIcon from './images/mail.png';
import wechatIcon from './images/wechat.svg';
import douyinIcon from './images/douyin.svg';
import xiaohongshuIcon from './images/xiaohongshu.svg';
import wechatQrcode from './images/wechat-qrcode.png';
import styles from './index.module.scss';

interface FooterDemoProps {
  theme?: 'light' | 'dark';
  countryCode: string;
}

const APP_STORE_DOWNLOAD_URL = 'https://roboneo-global.go.link/gQmVn';
const GOOGLE_PLAY_DOWNLOAD_URL = 'https://roboneo-global.go.link/9471I';

// 保留用户提供的完整主页链接及渠道参数，仅用于国内版社媒入口。
const DOMESTIC_SOCIAL_LINKS = [
  {
    name: '抖音',
    icon: douyinIcon,
    url: 'https://www.douyin.com/user/MS4wLjABAAAAX2xHwF2CMVtx2XNazpfBM4Ug2CUzTGssDus24k4Ldcc?from_tab_name=main',
  },
  {
    name: '小红书',
    icon: xiaohongshuIcon,
    url: 'https://www.xiaohongshu.com/user/profile/652146a00000000023027009?xsec_token=ABOFgCxHGq1el3-hxYPfOQFN6_2r-Kh7Vem3k_FUvhBo8=&xsec_source=pc_search',
  },
];

// 国内导航采用本需求提供的原文与路径，不修改 Smartcat 语言包。
const DOMESTIC_LINK_GROUPS = [
  {
    title: '功能',
    items: [
      { text: '画质修复', url: '/ai-enhance' },
      { text: 'AI去水印', url: '/ai-watermark' },
      { text: 'AI消除', url: '/ai-eraser' },
      { text: 'AI抠图', url: '/ai-cutout' },
    ],
  },
  {
    title: '帮助与支持',
    items: [
      { text: '如何使用RoboNeo CLI', url: '/cli/zh' },
      { text: '用户条款', url: 'https://www.roboneo.com/user-agreement?countryCode=CN&theme=dark' },
      { text: '隐私协议', url: 'https://www.roboneo.com/privacy-policy?countryCode=CN&theme=dark' },
    ],
  },
];

/**
 * Footer 导航的 rel：本站页只加 noopener，避免新标签反向劫持且保留内链权重；外链仍用 nofollow noreferrer。
 */
function getFooterNavRel(url: string): string {
  try {
    const hostname = new URL(url, 'https://www.roboneo.com').hostname.replace(/^www\./, '');
    if (hostname === 'roboneo.com') {
      return 'noopener';
    }
  } catch {
    return 'noopener';
  }
  return 'nofollow noreferrer';
}

/** 官网页脚按请求地区展示导航；海外版保留原有链接、下载入口和布局。 */
export default function Footer({ theme = 'dark', countryCode = 'US' }: FooterDemoProps) {
  const officialHome = useOfficialHomeContext();
  // 与官网入口保持一致，港澳台也使用国内导航，避免 SSR 和水合后内容不一致。
  const isDomestic = !getIsOverseaByCountryCode((officialHome?.countryCode ?? countryCode).trim().toUpperCase() || 'CN');
  const mediumList = [
    {
      name: 'Mail',
      icon: mailIcon.src,
      isMail: true,
      url: 'support@roboneo.com',
    },
    {
      name: 'X',
      icon: xIcon.src,
      url: 'https://x.com/RoboNeo_ai',
    },
    {
      name: 'Discord',
      icon: discordIcon.src,
      url: ' https://discord.gg/JXt2gwDpVp',
    },
    // {
    //   name: 'Instagram',
    //   icon: insIcon.src,
    //   url: '',
    // },

    // {
    //   name: 'YouTube',
    //   icon: youtubeIcon.src,
    //   url: '',
    // },
    // {
    //   name: 'TikTok',
    //   icon: tiktokIcon.src,
    //   url: '',
    // },

  ];

  const featureList = [
    {
      text: 'AI Image Editor',
      url: 'https://www.roboneo.com/ai-image-editor',
    },
    {
      text: 'AI Anime Generator',
      url: 'https://www.roboneo.com/ai-anime-generator',
    },
    {
      text: 'AI Poster',
      url: 'https://www.roboneo.com/ai-poster-generator',
    },
    {
      text: 'AI Ad Generator',
      url: 'https://www.roboneo.com/ai-advertising-generator',
    },
    {
      text: 'AI Product Photography',
      url: 'https://www.roboneo.com/ai-product-photography-agent',
    },
  ];

  const imageModelList = [
    {
      text: 'Nano Banana 2',
      url: 'https://www.roboneo.com/nano-banana-2',
    },
    {
      text: 'Gemini 3.1',
      url: 'https://www.roboneo.com/gemini-3-0-image-video-understanding',
    },
    {
      text: 'Flux Knotext',
      url: 'https://www.roboneo.com/flux-kontext-image-editing',
    },
    // {
    //   text: 'Nano Banana Pro',
    //   url: 'https://www.roboneo.com/robonoe-nanobanana2-free',
    // },
  ];

  const videoModelList = [
    {
      text: 'Seedance 2.0',
      url: 'https://www.roboneo.com/seedance2',
    },
    // {
    //   text: 'Kling 2.5',
    //   url: 'https://www.roboneo.com/kling-video-generator',
    // },
    {
      text: 'Kling 3.0',
      url: 'https://www.roboneo.com/kling-3',
    },
    {
      text: 'Sora 2',
      url: 'https://www.roboneo.com/roboneo-sora2-video-generator',
    },
    {
      text: 'VEO 3.0',
      url: 'https://www.roboneo.com/veo3-video-generator',
    },
    {
      text: 'Wan 2.6',
      url: 'https://www.roboneo.com/wan-video-generator2',
    },
  ];

  const helpLegalList = [
    {
      text: 'Help Center',
      url: 'https://www.notion.so/cpp-roboneo/RoboNeo-Playbook-2a024b73201880ef886afdeb4864b7d0',
    },
    {
      text: officialHome?.messages.terms ?? 'Terms of Service',
      url: `https://www.roboneo.com/user-agreement?countryCode=${countryCode}&theme=dark`,
    },
    {
      text: officialHome?.messages.privacy ?? 'Privacy Policy',
      url: `https://www.roboneo.com/privacy-policy?countryCode=${countryCode}&theme=dark`,
    },
  ];

  return (
    <footer className={styles.footer} data-theme={theme}>
      <div className={styles.container}>
        <div className={styles.main}>
          <div className={styles.brand}>
            <div className={styles.logo} />
            <p className={styles.description}>
              {isDomestic ? '以IP为主角的AI创作平台' : 'AI Creative Agent'}
            </p>
            {/* 社媒列表 */}
            <ul className={isDomestic ? `${styles.mediumList} ${styles.domesticSocials}` : styles.mediumList}>
              {isDomestic ? (
                <>
                  <li>
                    <a href="mailto:support@roboneo.com" title="邮箱" target="_self">
                      <Image src={mailIcon} alt="邮箱" width={36} height={36} />
                    </a>
                  </li>
                  <li>
                    {/* 使用本次提供的二维码原图；悬停、键盘聚焦与触屏点击均可查看。 */}
                    <Popover
                      placement="top"
                      trigger={['hover', 'focus', 'click']}
                      overlayClassName="resetPopoverAndArrow"
                      content={(
                        <div className={styles.wechatQrcode}>
                          <Image src={wechatQrcode} alt="微信扫码加入官方社群" width={240} height={240} unoptimized />
                        </div>
                      )}
                    >
                      <button type="button" className={styles.socialButton} aria-label="微信" title="微信">
                        <Image src={wechatIcon} alt="" width={36} height={36} />
                      </button>
                    </Popover>
                  </li>
                  {DOMESTIC_SOCIAL_LINKS.map((item) => (
                    <li key={item.name}>
                      <a href={item.url} title={item.name} target="_blank" rel="noopener noreferrer">
                        <Image src={item.icon} alt={item.name} width={36} height={36} />
                      </a>
                    </li>
                  ))}
                </>
              ) : mediumList.map((item) => (
                <li key={item.name}>
                  <a href={item.isMail ? `mailto:${item.url}` : item.url} title={item.name} target={item.isMail ? '_self' : '_blank'} rel="noopener noreferrer">
                    <Image src={item.icon} alt={item.name} width={36} height={36} />
                  </a>
                </li>
              ))}
            </ul>
            {/* 两个应用商店入口分别跳转对应渠道的下载落地页。 */}
            <div className={styles.downloadList}>
              <a href={APP_STORE_DOWNLOAD_URL} aria-label="Download RoboNeo on the App Store" target="_blank" rel="noopener noreferrer">
                <Image src={appStoreBadge} alt="Download on the App Store" width={88} height={30} />
              </a>
              <a href={GOOGLE_PLAY_DOWNLOAD_URL} aria-label="Get RoboNeo on Google Play" target="_blank" rel="noopener noreferrer">
                <Image src={googlePlayBadge} alt="Get it on Google Play" width={88} height={30} />
              </a>
            </div>
          </div>

          <div className={isDomestic ? `${styles.links} ${styles.domesticLinks}` : styles.links}>
            {isDomestic ? DOMESTIC_LINK_GROUPS.map((group) => (
              <div className={styles.linkGroup} key={group.title}>
                {/* 导航分组标签仅作视觉分组，不进入页面标题层级。 */}
                <div className={styles.groupTitle}>{group.title}</div>
                <ul className={styles.linkList}>
                  {group.items.map((item) => (
                    <li key={item.url}>
                      <a href={item.url} rel={getFooterNavRel(item.url)} target="_blank">{item.text}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )) : (
              <>
                <div className={styles.linkGroup}>
                  <div className={styles.groupTitle}>Help & Legal</div>
                  <ul className={styles.linkList}>
                    {helpLegalList.map((item) => (
                      <li key={item.text}>
                        <a href={item.url} rel={getFooterNavRel(item.url)} target="_blank">{item.text}</a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={styles.linkGroup}>
                  <div className={styles.groupTitle}>{officialHome?.messages.features ?? 'Features'}</div>
                  <ul className={styles.linkList}>
                    {featureList.map((item) => (
                      <li key={item.text}>
                        <a href={item.url} rel={getFooterNavRel(item.url)} target="_blank">{item.text}</a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={styles.linkGroup}>
                  <div className={styles.groupTitle}>Image Models</div>
                  <ul className={styles.linkList}>
                    {imageModelList.map((item) => (
                      <li key={item.text}>
                        <a href={item.url} rel={getFooterNavRel(item.url)} target="_blank">{item.text}</a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={styles.linkGroup}>
                  <div className={styles.groupTitle}>Video Models</div>
                  <ul className={styles.linkList}>
                    {videoModelList.map((item) => (
                      <li key={item.text}>
                        <a href={item.url} rel={getFooterNavRel(item.url)} target="_blank">{item.text}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>

        <div className={styles.bottom}>
          <p className={styles.copyright}>
            Copyright © 2026 Meitu Technology Pty Ltd
          </p>
        </div>
      </div>
    </footer>
  );
}
